import { useI18n } from "@/hooks/useI18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEOHead } from "@/components/SEOHead";
import { 
  ClipboardCheck, 
  Truck, 
  Thermometer, 
  FileText, 
  Plane, 
  CheckCircle,
  Calendar,
  Download,
  Phone
} from "lucide-react";

export default function Solutions() {
  const { t } = useI18n();

  const steps = [
    {
      icon: ClipboardCheck,
      title: t('solutions.step1.title'),
      description: t('solutions.step1.desc'),
      details: [
        t('solutions.step1.detail1'),
        t('solutions.step1.detail2'),
        t('solutions.step1.detail3'),
        t('solutions.step1.detail4')
      ]
    },
    {
      icon: CheckCircle,
      title: t('solutions.step2.title'),
      description: t('solutions.step2.desc'),
      details: [
        t('solutions.step2.detail1'),
        t('solutions.step2.detail2'),
        t('solutions.step2.detail3'),
        t('solutions.step2.detail4')
      ]
    },
    {
      icon: Thermometer,
      title: t('solutions.step3.title'),
      description: t('solutions.step3.desc'),
      details: [
        t('solutions.step3.detail1'),
        t('solutions.step3.detail2'),
        t('solutions.step3.detail3'),
        t('solutions.step3.detail4')
      ]
    },
    {
      icon: FileText,
      title: t('solutions.step4.title'),
      description: t('solutions.step4.desc'),
      details: [
        t('solutions.step4.detail1'),
        t('solutions.step4.detail2'),
        t('solutions.step4.detail3'),
        t('solutions.step4.detail4'),
        t('solutions.step4.detail5'),
        t('solutions.step4.detail6')
      ]
    },
    {
      icon: Plane,
      title: t('solutions.step5.title'),
      description: t('solutions.step5.desc'),
      details: [
        t('solutions.step5.detail1'),
        t('solutions.step5.detail2'),
        t('solutions.step5.detail3'),
        t('solutions.step5.detail4')
      ]
    },
    {
      icon: Truck,
      title: t('solutions.step6.title'),
      description: t('solutions.step6.desc'),
      details: [
        t('solutions.step6.detail1'),
        t('solutions.step6.detail2'),
        t('solutions.step6.detail3'),
        t('solutions.step6.detail4')
      ]
    }
  ];

  const benefits = [
    t('solutions.benefit1'),
    t('solutions.benefit2'),
    t('solutions.benefit3'),
    t('solutions.benefit4'),
    t('solutions.benefit5'),
    t('solutions.benefit6')
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-display font-light mb-6 tracking-wide">
              {t('page.solutions.title')}
            </h1>
            <p className="text-lg md:text-xl mb-8 text-andean-cream/90 font-light leading-relaxed">
              {t('solutions.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              {t('solutions.process.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('solutions.process.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="relative h-full hover:shadow-andean transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-andean rounded-lg flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-andean-cream" />
                    </div>
                    <Badge variant="outline" className="text-sm">
                      {t('step')} {index + 1}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl">{step.title}</CardTitle>
                  <CardDescription className="text-base">
                    {step.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-andean-forest flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-8 tracking-wide">
              {t('solutions.benefits.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-background rounded-lg shadow-sm">
                  <CheckCircle className="w-6 h-6 text-andean-forest flex-shrink-0" />
                  <span className="text-foreground font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}